import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { Mic2, Headphones, Radio, AudioLines } from "lucide-react";

/**
 * Reusable, on-brand ambient motion pieces:
 * - ScrollProgressBar  → thin cyan bar pinned to the top of the viewport that
 *   fills as the user scrolls the page.
 * - AudioSpectrumDivider → 32 vertical bars pulsing at different phases; use
 *   as a decorative divider between sections.
 * - FloatingOrbs → 3 slowly drifting color orbs; drop into any section as an
 *   ambient background (absolute-positioned, behind content).
 *
 * PARALLAX: Every decorative component below runs its outermost wrapper
 * through `useParallaxY`, which returns a viewport-scoped ref + motion value.
 * The element translates only while it's inside the viewport — total offset is
 * bounded to `range` px, so parallax never extends the page's scroll height.
 * Opacities, colors and existing intrinsic animations are untouched.
 */

/**
 * useParallaxY — returns [ref, y] where `ref` attaches to the element you want
 * to parallax and `y` is a MotionValue that goes 0 → range as the element
 * scrolls from the bottom of the viewport up through the top. `range` is the
 * TOTAL offset in pixels (positive = element lags behind, moving downward
 * relative to the page). Kept modest (40–120px) so parallax reads as depth,
 * not motion, and never inflates page scroll height.
 */
const useParallaxY = (range = 80) => {
  const ref = useRef(null);
  // Skip parallax entirely on mobile and tablet to eliminate scroll jank and
  // glitches. Only run parallax on desktop (>=1024px).
  const isBelowDesktop =
    typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, isBelowDesktop ? 0 : range]);
  return [ref, y];
};

export const ScrollProgressBar = () => {
  const { scrollYProgress } = useScroll();
  const width = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    restDelta: 0.001,
  });
  return (
    <motion.div
      data-testid="scroll-progress-bar"
      aria-hidden="true"
      style={{ scaleX: width, transformOrigin: "0% 50%" }}
      className="fixed top-0 left-0 right-0 h-[2px] z-[120] bg-gradient-to-r from-[#22c55e] via-[#00f0ff] to-[#ff0055] pointer-events-none"
    />
  );
};

/**
 * 32-bar audio spectrum, each bar with a random-but-deterministic phase so the
 * "waveform" never looks scripted. Very subtle by default — bumps up in
 * lightness when the section it sits in is on-screen.
 */
export const AudioSpectrumDivider = ({ intensity = 0.55 }) => {
  const BARS = 40;
  const [ref, y] = useParallaxY(60);
  // Deterministic phase/height offsets — computed once
  const phases = useRef(
    Array.from({ length: BARS }, (_, i) => ({
      height: 25 + ((i * 37) % 55),
      delay: (i * 0.13) % 1.6,
      duration: 1.4 + ((i * 0.09) % 1.2),
    })),
  ).current;

  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      data-testid="audio-spectrum-divider"
      className="relative w-full max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14"
      style={{ opacity: intensity, y }}
    >
      <div className="flex items-center justify-center gap-[3px] h-16 md:h-20">
        {phases.map((p, i) => (
          <motion.span
            key={i}
            className="inline-block w-[3px] md:w-[4px] rounded-full"
            style={{
              background:
                i % 3 === 0
                  ? "linear-gradient(to top, #22c55e, #00f0ff)"
                  : i % 3 === 1
                    ? "linear-gradient(to top, #00f0ff, #a78bfa)"
                    : "linear-gradient(to top, #a78bfa, #ff0055)",
            }}
            animate={{ height: [`${p.height * 0.35}%`, `${p.height}%`, `${p.height * 0.5}%`] }}
            transition={{
              duration: p.duration,
              delay: p.delay,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

/**
 * Floating background orbs — drop into any container as ambient decoration.
 * Sits absolute-positioned so parent must be relative + no overflow.
 */
export const FloatingOrbs = ({
  colors = ["#00f0ff", "#ff0055", "#a78bfa"],
  className = "",
}) => {
  const [ref, y] = useParallaxY(90);
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      data-testid="floating-orbs"
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{ y }}
    >
      {colors.map((c, i) => (
        <motion.div
          key={c + i}
          className="absolute rounded-full blur-3xl w-[220px] h-[220px] md:w-[300px] md:h-[300px] lg:w-[340px] lg:h-[340px]"
          style={{
            background: c,
            opacity: 0.08,
            left: `${20 + i * 30}%`,
            top: `${15 + i * 20}%`,
          }}
          animate={{
            x: [0, 40, -25, 0],
            y: [0, -35, 20, 0],
            scale: [1, 1.12, 0.95, 1],
          }}
          transition={{
            duration: 18 + i * 4,
            ease: "easeInOut",
            repeat: Infinity,
            delay: i * 2,
          }}
        />
      ))}
    </motion.div>
  );
};

/**
 * A single "listening" pulse dot with concentric rings — great for
 * anchor accents (e.g., "live" indicators near section headings).
 * NOTE: inline UI accent — no parallax (would break layout).
 */
export const PulseDot = ({ color = "#00f0ff", size = 10 }) => (
  <span
    className="relative inline-flex"
    style={{ width: size, height: size }}
    aria-hidden="true"
  >
    <span
      className="absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping"
      style={{ backgroundColor: color }}
    />
    <span
      className="relative inline-flex w-full h-full rounded-full"
      style={{ backgroundColor: color }}
    />
  </span>
);

/**
 * Parallax accent line — a thin gradient line that shifts x-position as the
 * user scrolls, adding depth without stealing attention. Give it a `sectionRef`.
 * (Already scroll-linked via its own useScroll — no additional wrapper needed.)
 */
export const ParallaxAccentLine = ({ sectionRef, side = "left" }) => {
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"]);
  return (
    <motion.div
      aria-hidden="true"
      data-testid="parallax-accent-line"
      style={{ y }}
      className={`absolute top-0 bottom-0 w-[1px] pointer-events-none ${
        side === "left" ? "left-4" : "right-4"
      }`}
    >
      <div
        className="w-full h-full"
        style={{
          background:
            "linear-gradient(to bottom, transparent, rgba(0,240,255,0.35), rgba(255,0,85,0.25), transparent)",
        }}
      />
    </motion.div>
  );
};

/** Convenience: a tiny scrolling equalizer icon (5 bars) for footer / nav accents.
 *  Inline UI accent — no parallax. */
export const MiniEqualizer = ({ color = "#00f0ff" }) => {
  const bars = [0.4, 0.9, 0.5, 0.8, 0.6];
  return (
    <span
      className="inline-flex items-end gap-[2px] h-4"
      aria-hidden="true"
      data-testid="mini-equalizer"
    >
      {bars.map((h, i) => (
        <motion.span
          key={i}
          className="inline-block w-[2px] rounded-full"
          style={{ background: color, height: `${h * 100}%` }}
          animate={{ height: [`${h * 30}%`, `${h * 100}%`, `${h * 50}%`] }}
          transition={{
            duration: 0.8 + i * 0.15,
            delay: i * 0.08,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      ))}
    </span>
  );
};

/* -------------------------------------------------------------------------
 * Corner overlays — small decorative motion pieces that live ON TOP of
 * existing photos/videos/cards, tucked into a corner. Meant to add subtle
 * kinetic charm without stealing focus. Each component takes a `corner`
 * prop: "tl" | "tr" | "bl" | "br" (default "tr") and a `color`.
 * ------------------------------------------------------------------------- */
const cornerClass = (c) =>
  ({
    tl: "top-3 left-3",
    tr: "top-3 right-3",
    bl: "bottom-3 left-3",
    br: "bottom-3 right-3",
  })[c] || "top-3 right-3";

/**
 * A soft diagonal light-leak that pulses in opacity. Perfect for image /
 * video corners. Uses linear-gradient at 45deg with animated opacity.
 */
export const LightLeak = ({
  color = "#ff8a1e",
  corner = "tr",
  size = 240,
}) => {
  const [ref, y] = useParallaxY(40);
  const originMap = {
    tl: "0% 0%",
    tr: "100% 0%",
    bl: "0% 100%",
    br: "100% 100%",
  };
  const origin = originMap[corner] || originMap.tr;
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      className={`absolute ${cornerClass(corner)} pointer-events-none`}
      style={{ y }}
    >
      <motion.div
        data-testid="light-leak"
        className="rounded-full"
        style={{
          width: size,
          height: size,
          background: `radial-gradient(circle at ${origin}, ${color}55, ${color}00 65%)`,
          filter: "blur(24px)",
        }}
        animate={{ opacity: [0.35, 0.85, 0.5] }}
        transition={{
          duration: 7,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "reverse",
        }}
      />
    </motion.div>
  );
};

/** An animated podcast mic silhouette with a subtle bob + glow */
export const CornerMic = ({ color = "#00f0ff", corner = "tr", size = 34 }) => {
  const [ref, y] = useParallaxY(40);
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      className={`absolute ${cornerClass(corner)} pointer-events-none`}
      style={{ y }}
    >
      <motion.div
        data-testid="corner-mic"
        style={{ color }}
        initial={{ opacity: 0.7 }}
        animate={{ y: [0, -4, 0], opacity: [0.7, 1, 0.7] }}
        transition={{
          duration: 4.5,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        <div
          className="rounded-full p-2 backdrop-blur-md"
          style={{
            background: `${color}22`,
            border: `1px solid ${color}55`,
            boxShadow: `0 0 20px ${color}55`,
          }}
        >
          <Mic2 size={size * 0.55} strokeWidth={1.5} />
        </div>
      </motion.div>
    </motion.div>
  );
};

/** Headphones silhouette with a slow rotation */
export const CornerHeadphones = ({
  color = "#a78bfa",
  corner = "br",
  size = 40,
}) => {
  const [ref, y] = useParallaxY(40);
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      className={`absolute ${cornerClass(corner)} pointer-events-none`}
      style={{ y }}
    >
      <motion.div
        data-testid="corner-headphones"
        style={{ color }}
        animate={{ rotate: [-4, 4, -4], opacity: [0.6, 0.95, 0.6] }}
        transition={{
          duration: 5,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        <div
          className="rounded-full p-2.5 backdrop-blur-md"
          style={{
            background: `${color}1a`,
            border: `1px solid ${color}55`,
          }}
        >
          <Headphones size={size * 0.55} strokeWidth={1.5} />
        </div>
      </motion.div>
    </motion.div>
  );
};

/** Small 6-bar waveform inside a rounded pill (tucks into any corner) */
export const CornerWaveform = ({
  color = "#22c55e",
  corner = "bl",
  bars = 7,
}) => {
  const [ref, y] = useParallaxY(35);
  const heights = Array.from({ length: bars }, (_, i) => 0.35 + ((i * 0.19) % 0.65));
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      className={`absolute ${cornerClass(corner)} pointer-events-none`}
      style={{ y }}
    >
      <div
        data-testid="corner-waveform"
        className="wav-anim wav-anim-pulse flex items-center gap-2 px-2.5 py-1.5 rounded-full backdrop-blur-md"
        style={{
          background: `${color}1a`,
          border: `1px solid ${color}55`,
        }}
      >
        <span className="flex items-end gap-[2px] h-4">
          {heights.map((h, i) => (
            <span
              key={i}
              className="wav-anim-bar inline-block w-[2px] rounded-full"
              style={{
                background: color,
                height: `${h * 100}%`,
                transformOrigin: "bottom center",
                "--wav-dur": `${(0.9 + i * 0.12).toFixed(2)}s`,
                "--wav-delay": `${(i * 0.06).toFixed(2)}s`,
              }}
            />
          ))}
        </span>
        <AudioLines size={12} style={{ color }} />
      </div>
    </motion.div>
  );
};

/** A field of slowly drifting dust motes — subtle depth in sections that
 * have no photos/videos of their own. Uses randomised (but deterministic)
 * particle positions so it never looks scripted. */
export const AmbientDust = ({ count = 22, color = "#00f0ff" }) => {
  const [ref, y] = useParallaxY(70);
  const particles = useRef(
    Array.from({ length: count }, (_, i) => ({
      x: (i * 47) % 100,
      y: (i * 71) % 100,
      size: 1 + ((i * 3) % 4),
      duration: 10 + ((i * 1.7) % 12),
      delay: (i * 0.4) % 5,
    })),
  ).current;

  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      data-testid="ambient-dust"
      className="absolute inset-0 pointer-events-none"
      style={{ y }}
    >
      {particles.map((p, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: color,
            boxShadow: `0 0 ${p.size * 4}px ${color}`,
            opacity: 0.4,
          }}
          animate={{
            y: [0, -30, 15, 0],
            x: [0, 20, -15, 0],
            opacity: [0.25, 0.7, 0.35, 0.25],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: "easeInOut",
            repeat: Infinity,
          }}
        />
      ))}
    </motion.div>
  );
};

/** Animated horizontal audio-scrub line — thin bar that fills 0→100% and
 * loops. Sits inside any container with `position: relative`. Great as a
 * micro-accent under a section heading or a card.
 * NOTE: inline UI accent — no parallax (would break layout). */
export const ScrubLine = ({ color = "#00f0ff", duration = 3.4 }) => (
  <motion.div
    aria-hidden="true"
    data-testid="scrub-line"
    className="relative h-[2px] w-full overflow-hidden rounded-full"
    style={{ background: `${color}22` }}
  >
    <motion.span
      className="absolute inset-y-0 left-0 rounded-full"
      style={{
        background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
        width: "40%",
      }}
      animate={{ x: ["-100%", "250%"] }}
      transition={{ duration, ease: "linear", repeat: Infinity }}
    />
  </motion.div>
);

/** Vinyl-record spinner (SVG). Slow continuous rotation. Great as a decorative
 * bg element in copy-only sections. */
export const SpinningRecord = ({ size = 100, color = "#00f0ff", speed = 40 }) => {
  const [ref, y] = useParallaxY(100);
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      style={{ y, opacity: 0.35 }}
      className="inline-block"
    >
      <motion.svg
        data-testid="spinning-record"
        width={size}
        height={size}
        viewBox="0 0 100 100"
        animate={{ rotate: 360 }}
        transition={{ duration: speed, ease: "linear", repeat: Infinity }}
      >
        <circle cx="50" cy="50" r="48" fill="none" stroke={color} strokeWidth="1" opacity="0.4" />
        <circle cx="50" cy="50" r="38" fill="none" stroke={color} strokeWidth="0.5" opacity="0.35" />
        <circle cx="50" cy="50" r="28" fill="none" stroke={color} strokeWidth="0.5" opacity="0.3" />
        <circle cx="50" cy="50" r="18" fill="none" stroke={color} strokeWidth="0.5" opacity="0.25" />
        <circle cx="50" cy="50" r="6" fill={color} opacity="0.6" />
        <circle cx="50" cy="50" r="1.5" fill="#05050a" />
      </motion.svg>
    </motion.div>
  );
};
/** Broadcasting radio ripple — concentric circles emanating outward */
export const BroadcastRipple = ({
  color = "#ff0055",
  corner = "tl",
  size = 42,
}) => {
  const [ref, y] = useParallaxY(45);
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      data-testid="broadcast-ripple"
      className={`absolute ${cornerClass(corner)} pointer-events-none`}
      style={{ y }}
    >
      <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="absolute rounded-full border"
            style={{ borderColor: color, width: size, height: size }}
            animate={{ scale: [0.6, 1.5], opacity: [0.75, 0] }}
            transition={{
              duration: 2.6,
              delay: i * 0.85,
              ease: "easeOut",
              repeat: Infinity,
            }}
          />
        ))}
        <div
          className="relative rounded-full p-2 backdrop-blur-md"
          style={{
            background: `${color}22`,
            border: `1px solid ${color}55`,
            color,
          }}
        >
          <Radio size={size * 0.4} strokeWidth={1.6} />
        </div>
      </div>
    </motion.div>
  );
};

/** Hook: cleans up interval on unmount — reused across the file */
export const useCleanupInterval = (fn, ms) => {
  useEffect(() => {
    const id = setInterval(fn, ms);
    return () => clearInterval(id);
  }, [fn, ms]);
};

/* -------------------------------------------------------------------------
 * Layered "watermark" background pieces — like the Hero's "Scroll to explore"
 * text but with varied treatments for other sections. All are absolute /
 * pointer-events-none / low-opacity. Drop into any `position: relative`
 * section.
 * ------------------------------------------------------------------------- */

/** Massive translucent word sitting behind a section corner. Slow drift. */
export const HugeWatermarkText = ({
  text = "STUDIO",
  corner = "br",
  color = "#00f0ff",
  size = 220,
}) => {
  const [ref, y] = useParallaxY(120);
  const positionMap = {
    tl: "top-4 left-4 text-left",
    tr: "top-4 right-4 text-right",
    bl: "bottom-4 left-4 text-left",
    br: "bottom-4 right-4 text-right",
  };
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      className={`absolute ${positionMap[corner] || positionMap.br} pointer-events-none overflow-hidden max-w-full`}
      style={{ y }}
    >
      <motion.div
        data-testid="huge-watermark-text"
        className="font-display font-black tracking-tighter select-none"
        style={{
          fontSize: `clamp(28px, 8vw, ${size}px)`,
          lineHeight: 0.85,
          color,
          opacity: 0.05,
          WebkitTextStroke: `1px ${color}`,
          WebkitTextFillColor: "transparent",
          whiteSpace: "nowrap",
        }}
        animate={{ y: [0, -12, 0], opacity: [0.04, 0.09, 0.04] }}
        transition={{ duration: 10, ease: "easeInOut", repeat: Infinity }}
      >
        {text}
      </motion.div>
    </motion.div>
  );
};

/** Vertical scrolling text ticker — like an old-school ON-AIR strip running
 *  up the side of a section. */
export const VerticalTextTicker = ({
  items = ["ON AIR", "RECORDING", "LIVE", "CH 01", "STEREO"],
  side = "left",
  color = "#22c55e",
  duration = 22,
}) => {
  const [ref, y] = useParallaxY(55);
  const loop = [...items, ...items];
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      data-testid="vertical-text-ticker"
      className={`absolute top-0 bottom-0 ${side === "left" ? "left-4" : "right-4"} pointer-events-none overflow-hidden w-6`}
      style={{ y }}
    >
      <motion.div
        className="flex flex-col gap-8 whitespace-nowrap"
        style={{
          writingMode: "vertical-rl",
          textOrientation: "mixed",
          fontFamily: "monospace",
          fontSize: 10,
          letterSpacing: "0.4em",
          color,
          opacity: 0.5,
        }}
        animate={{ y: ["0%", "-50%"] }}
        transition={{ duration, ease: "linear", repeat: Infinity }}
      >
        {loop.map((t, i) => (
          <span key={i} className="inline-block">
            {t} <span style={{ opacity: 0.4 }}>·</span>
          </span>
        ))}
      </motion.div>
    </motion.div>
  );
};

/** REC badge with a live-updating mm:ss timer. Sits in any corner. */
export const RecordingBadge = ({ corner = "tl", color = "#ff0055" }) => {
  const [ref, y] = useParallaxY(40);
  const [sec, setSec] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSec((s) => (s + 1) % 3600), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(sec / 60)).padStart(2, "0");
  const ss = String(sec % 60).padStart(2, "0");
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      data-testid="recording-badge"
      className={`absolute ${cornerClass(corner)} pointer-events-none inline-flex items-center gap-2 px-2.5 py-1 rounded-full backdrop-blur-md`}
      style={{
        background: `${color}22`,
        border: `1px solid ${color}55`,
        color,
        fontFamily: "monospace",
        fontSize: 11,
        letterSpacing: "0.14em",
        y,
      }}
    >
      <motion.span
        className="inline-block rounded-full"
        style={{ width: 8, height: 8, background: color }}
        animate={{ opacity: [1, 0.25, 1] }}
        transition={{ duration: 1.2, ease: "easeInOut", repeat: Infinity }}
      />
      <span>REC</span>
      <span style={{ opacity: 0.75 }}>
        {mm}:{ss}
      </span>
    </motion.div>
  );
};

/** Circular orbiting text — text arranged around a circle, slowly rotating.
 *  Great as a decorative watermark near CTAs. */
export const CircularText = ({
  text = "SB MEDIA · STUDIO · ",
  size = 200,
  color = "#00f0ff",
  duration = 30,
}) => {
  const [ref, y] = useParallaxY(85);
  const chars = text.repeat(2).split("");
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      className="absolute pointer-events-none"
      style={{ width: size, height: size, y }}
    >
      <motion.div
        data-testid="circular-text"
        className="select-none"
        style={{ width: size, height: size, opacity: 0.5 }}
        animate={{ rotate: 360 }}
        transition={{ duration, ease: "linear", repeat: Infinity }}
      >
        {chars.map((c, i) => {
          const angle = (i / chars.length) * 360;
          return (
            <span
              key={i}
              className="absolute left-1/2 top-1/2 font-mono"
              style={{
                transformOrigin: `0 ${size / 2}px`,
                transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-${size / 2 - 10}px)`,
                fontSize: 11,
                letterSpacing: "0.2em",
                color,
                opacity: 0.6,
              }}
            >
              {c}
            </span>
          );
        })}
      </motion.div>
    </motion.div>
  );
};

/** Frequency/bitrate ticker — horizontal scrolling text like "128 · 192 · 256 · 320 kbps" */
export const FrequencyTicker = ({
  items = ["44.1kHz", "48kHz", "96kHz", "192kHz", "24-bit", "32-bit float"],
  color = "#22c55e",
  duration = 30,
  corner = "tr",
}) => {
  const [ref, y] = useParallaxY(50);
  const loop = [...items, ...items, ...items];
  const cornerCls = {
    tr: "top-4 right-4",
    tl: "top-4 left-4",
    br: "bottom-4 right-4",
    bl: "bottom-4 left-4",
  }[corner];
  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      data-testid="frequency-ticker"
      className={`absolute ${cornerCls} pointer-events-none overflow-hidden max-w-[280px]`}
      style={{ opacity: 0.55, y }}
    >
      <motion.div
        className="flex gap-6 whitespace-nowrap font-mono"
        style={{ fontSize: 10, letterSpacing: "0.3em", color }}
        animate={{ x: ["0%", "-33.333%"] }}
        transition={{ duration, ease: "linear", repeat: Infinity }}
      >
        {loop.map((t, i) => (
          <span key={i} className="inline-block">
            {t} <span style={{ opacity: 0.4 }}>·</span>
          </span>
        ))}
      </motion.div>
    </motion.div>
  );
};
